import { observable, computed, action } from 'mobx';
import Alert from 'react-s-alert';

import {
  App,
  Input,
  ArrayApp,
  AppRegistry,
  ModelRegistry,
  ComponentRegistry,
} from 'core/index';

import { PermissionedModel } from 'apps/permissions/app';
import { FormStore } from 'libs/forms/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { ThreeColumnLayout } from 'apps/layout';
import { conf as tagsConf } from 'apps/tag/config.yaml';
import { raiseUserError } from 'apps/utils';

import { conf } from 'apps/collection/config.yaml';

import i18next from 'i18next';

export class CollectionModel extends PermissionedModel {
  static registryName = 'CollectionModel';
  static ModelProps = {
    creatorProfile: 'ProfileModel',
    tags: ['TagModel'],
    content: 'ContentModel',
    images: ['FileModel'],
  };

  @observable name = '';
  @observable description = '';
  @observable projectsCount = 0;
  @observable creatorProfile = null;
  @observable tags = [];
  @observable images = [];
  @observable space = '';
  @observable content = null;
  @observable slug = '';

  // FIXME
  // eslint-disable-next-line
  @computed get spacePath() {
    if (this.space) {
      let prefix;
      if (this.space.spaceType === 'PROFILE') {
        prefix = '@';
      } else if (this.space.spaceType === 'INITIATIVE') {
        prefix = '+';
      }
      return prefix + this.space.content.slug;
    }
  }
}
ModelRegistry.add(CollectionModel);

export class CollectionApp extends App {
  static registryName = 'CollectionApp';
  static AppProps = {
    creatorProfile: 'ProfileApp',
    tags: 'TagsApp',
    content: 'ContentApp',
    images: 'FilesApp',
  };

  static Model = CollectionModel;
  init() {
    this.form = new FormStore(this, conf, 'collectionForm');
    if (modalStore.props && Object.keys(modalStore.props).length !== 0) {
      this.openAddToCollectionAfterCreation = true;
    }
  }

  @observable editing = false;
  @observable saving = false;
  @observable openAddToCollectionAfterCreation = false;
  @computed get projectIsInCollection() {
    if (modalStore.props.projectCollections) {
      const projectInCollections = modalStore.props.projectCollections.filter(
        collection => collection.id === this.id,
      );
      if (projectInCollections.length > 0) {
        return true;
      }
    }
    return false;
  }

  @action SubmitCollection = () => {
    if (this.form.isValid) {
      this.form.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.SubmitCollection({
        onSysErr: this.onCreateError,
        onUserErr: this.onCreateError,
        onSuccess: this.onCreateSuccess,
      });
    } else {
      this.form.alert();
    }
  };

  @action onCreateError = error => {
    this.form.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'create', 'Collection');
  };

  @action onCreateSuccess = () => {
    this.form.SetPosting(false);
    this.form.clearDraft();
    clientStore.SetPosting(false);
    if (this.openAddToCollectionAfterCreation) {
      const AddToCollectionModal = ComponentRegistry.get(
        'modals.AddToCollectionModal',
      );
      modalStore.SetModalComponent(AddToCollectionModal, modalStore.props);
    } else {
      modalStore.ClearComponent();
    }
  };

  @action AddProject = () => {
    this.collectionInput = new Input({
      id: () => this.id,
      name: () => this.name,
      description: () => this.description,
      projects: () => [
        { projectId: modalStore.props.projectId, deleted: false },
      ],
    });
    clientStore.SetPosting(true);
    this.manager.MutateProjects(this.collectionInput, {
      onSysErr: this.onAddProjectError,
      onUserErr: this.onAddProjectError,
      onSuccess: this.onAddProjectSuccess,
    });
  };

  @action onAddProjectError = error => {
    clientStore.SetPosting(false);
    raiseUserError(error, 'add', 'Project to this Collection');
  };

  @action onAddProjectSuccess = () => {
    clientStore.SetPosting(false);
    const { projectName } = modalStore.props;
    modalStore.ClearComponent();
    setTimeout(() => {
      const { name } = this;
      Alert.success(
        i18next.t('Added "{{projectName}}" to "{{name}}" 👍', {
          projectName,
          name,
        }),
      );
    }, 300);
  };

  @action RemoveProject = () => {
    this.collectionInput = new Input({
      id: () => this.id,
      name: () => this.name,
      description: () => this.description,
      projects: () => [
        { projectId: modalStore.props.projectId, deleted: true },
      ],
    });
    clientStore.SetPosting(true);
    this.manager.MutateProjects(this.collectionInput, {
      onSysErr: this.onRemoveProjectError,
      onUserErr: this.onRemoveProjectError,
      onSuccess: this.onRemoveProjectSuccess,
    });
  };

  @action onRemoveProjectError = error => {
    clientStore.SetPosting(false);
    raiseUserError(error, 'remove', 'Project to this Collection');
  };

  @action onRemoveProjectSuccess = () => {
    clientStore.SetPosting(false);
    const { projectName } = modalStore.props;
    const collectioName = this.name;
    modalStore.ClearComponent();
    setTimeout(() => {
      Alert.success(
        i18next.t(
          'Removed "{{projectName}}" from your Collection "{{collectioName}} 👍"',
          { projectName, collectioName },
        ),
      );
    }, 300);
  };

  @action Edit = () => {
    this.editing = true;
  };

  @action EditTags = () => {
    this.editTagsFormStore = new FormStore(
      this,
      tagsConf,
      'editCollectionTags',
    );
  };

  @action DoneEditingTags = () => {
    modalStore.ClearComponent();
    this.editTagsFormStore = null;
  };

  @action Cancel = () => {
    this.ResetLocals();
    this.editing = false;
  };

  @action Save = () => {
    this.saving = true;
    if (!this.form.isValid) {
      this.form.alert();
    } else {
      this.form.SetPosting(true);
      this.manager.SubmitCollection({
        onSysErr: this.onSaveError,
        onUserErr: this.onSaveError,
        onSuccess: this.onSaveSuccess,
      });
    }
  };

  @action onSaveError = error => {
    this.saving = false;
    raiseUserError(error, 'update', 'Collection');
    this.form.SetPosting(false);
  };

  @action onSaveSuccess = () => {
    this.editing = false;
    this.saving = false;
    this.ResetLocals();
    this.form.SetPosting(false);
    this.form.clearDraft();
    Alert.success(i18next.t('Your collection has been updated successfully.'));
  };

  @action Delete = () => {
    this.deleted = true;
    this.manager.SubmitCollection({
      onSysErr: this.OnDeleteError,
      onUserErr: this.OnDeleteError,
      onSuccess: this.OnDeleteSuccess,
    });
  };

  @action OnDeleteError = error => {
    raiseUserError(error, 'delete', 'Collection');
  };

  @action OnDeleteSuccess = () => {
    this.ResetLocals();
    clientStore.routerStore.router.navigate('profile', {
      profile: clientStore.routerStore.params.get('profile'),
    });
    Alert.success('Collection deleted successfully.');
  };
}
AppRegistry.add(CollectionApp);

export class CollectionArrayApp extends ArrayApp {
  static registryName = 'CollectionArrayApp';
  static ElementApp = CollectionApp;
  cardLayout = new ThreeColumnLayout();
}
